import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * @typedef {Object} Toast
 * @property {string} id - Unique toast ID
 * @property {string} message - Main toast text
 * @property {string} [description] - Optional secondary text
 * @property {'info'|'success'|'warning'|'error'} [type] - Toast type for styling
 * @property {number} [duration] - Auto-dismiss in ms (default 5000, 0 = no auto-dismiss)
 * @property {Object} [action] - Optional action button
 * @property {string} action.label - Button text
 * @property {Function} action.onClick - Button click handler
 */

const ToastContext = createContext({
  toasts: [],
  showToast: () => {},
  dismissToast: () => {},
});

let toastCounter = 0;
const MAX_TOASTS = 5;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const dismissToast = useCallback((id) => {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((options) => {
    const id = `toast-${++toastCounter}`;
    const duration = options.duration ?? 5000;
    const toast = { ...options, id };

    setToasts((prev) => {
      const next = [...prev, toast];
      if (next.length > MAX_TOASTS) {
        const evicted = next.slice(0, next.length - MAX_TOASTS);
        evicted.forEach((t) => {
          if (timersRef.current[t.id]) {
            clearTimeout(timersRef.current[t.id]);
            delete timersRef.current[t.id];
          }
        });
        return next.slice(next.length - MAX_TOASTS);
      }
      return next;
    });

    if (duration > 0) {
      timersRef.current[id] = setTimeout(() => {
        dismissToast(id);
      }, duration);
    }

    return id;
  }, [dismissToast]);

  const value = useMemo(() => ({ toasts, showToast, dismissToast }), [toasts, showToast, dismissToast]);

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export const useToast = () => useContext(ToastContext);

export default ToastContext;
